import { VolumeIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { InternalLink } from '@components/link';
import { DateDisplay, DateToNowDisplay, Typography } from '@components/typography';
import { type Volume } from '@generated/graphql';
import { formatSizeFromGigabytes } from '@helper-functions/formatSize';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import { ServerTag } from '../../server/ServerTag';

type VolumeDetailsModalProps = {
  volume?: Pick<Volume, 'name' | 'size' | 'volumeId' | 'description' | 'createdAt' | 'updatedAt'>;
};

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Stack flex={1}>
    <Typography
      variant="caption2"
      color="foreground.paper.light"
      sx={{ minWidth: 100, flexShrink: 0 }}
    >
      {label}
    </Typography>
    {children}
  </Stack>
);

export const VolumeDetailsModal = ({ volume }: VolumeDetailsModalProps) => {
  const { palette } = useTheme();
  const { organizationId } = useOrganizationContext();

  return (
    <>
      <Stack gap={1} mb={6}>
        <Group alignItems="center" gap={2}>
          <VolumeIcon size={21} color={palette.foreground.paper.default} />
          <Typography variant="h2" handleOverFlow sx={{ flex: 1, minWidth: 0 }}>
            {volume.name}
          </Typography>
        </Group>
        <ServerTag
          label={formatSizeFromGigabytes(Number(volume.size))}
          color="default"
          variant="caption2"
          sx={{ width: 'fit-content' }}
        />
      </Stack>
      <Stack gap={4} sx={{ maxWidth: '450px' }}>
        <Typography variant="body2" color="foreground.paper.light">
          {volume.description || 'No description'}
        </Typography>
        <Stack gap={2}>
          <Group gap={4}>
            <DetailRow label="Created">
              <DateDisplay date={volume.createdAt || ''} />
            </DetailRow>
            <DetailRow label="Last Updated">
              <DateToNowDisplay date={volume.updatedAt || ''} />
            </DetailRow>
          </Group>
          {volume.volumeId && (
            <InternalLink to={`/${organizationId}/volumes/${volume.volumeId}`}>
              Explore volume
            </InternalLink>
          )}
        </Stack>
      </Stack>
    </>
  );
};
